import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PALETTE as P } from '../tokens';
import { featureFlagsApi } from '../api/featureFlags';
import { isNativeIOS } from '../utils/platform';

/*
 * Install/education screen for the native iOS app.
 *
 * The Apple Watch / Apple Health path: HealthKit only works inside the
 * iOS app, so web users on an iPhone are routed here (from onboarding
 * screen 5 or the Wearables screen) instead of hitting a hidden or
 * dead-end Apple Health option.
 *
 * PUBLIC route by design — mid-onboarding users don't have
 * onboarding_completed_at yet, and ProtectedRoute bounces them off every
 * protected path back to /onboarding. The install URL comes from the
 * unauthenticated /feature-flags endpoint (ios_app_install_url — App
 * Store listing once live; empty until then, which hides the CTA and
 * shows the rolling-out fallback), so no session is needed here.
 *
 * ?from= tells the "keep going" link where to return:
 *   onboarding → /onboarding?resume=6 (draft-rehydrated, see
 *                utils/onboardingDraft.js)
 *   wearables  → /wearables
 *   otherwise  → history back
 */

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .gta-root {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    min-height: 100dvh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fu  { animation: fadeUp 0.48s ease forwards; }
  .d1  { animation-delay: 0.08s; opacity: 0; }
  .d2  { animation-delay: 0.20s; opacity: 0; }

  .gta-btn {
    display: block;
    width: 100%;
    padding: 17px;
    background: ${P.dark};
    border: none;
    border-radius: 16px;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px;
    font-style: italic;
    color: ${P.bg};
    text-align: center;
    text-decoration: none;
    transition: opacity 0.15s;
    letter-spacing: 0.01em;
  }
  .gta-btn:hover { opacity: 0.85; }
`;

export default function GetTheApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = new URLSearchParams(location.search).get('from');

  // Install URL from the public feature-flags bag. Empty (not yet set,
  // fetch failed, or app not live) → hide the CTA, show the fallback.
  const [installUrl, setInstallUrl] = useState('');
  useEffect(() => {
    featureFlagsApi
      .get()
      .catch(() => ({}))
      .then((flags) => setInstallUrl(flags?.ios_app_install_url || ''));
  }, []);

  const handleKeepGoing = () => {
    if (from === 'onboarding') {
      // Resume onboarding at the wearable step — the draft saved before
      // navigating here rehydrates the answers (see Onboarding.jsx).
      // Screen 6 since the AI-consent step shifted the sequence by one.
      navigate('/onboarding?resume=6');
    } else if (from === 'wearables') {
      navigate('/wearables');
    } else {
      navigate(-1);
    }
  };

  // Inside the native shell the app is, by definition, already installed —
  // nothing to link to. (Reachable only by typing the URL by hand.)
  const alreadyInApp = isNativeIOS();

  return (
    <>
      <style>{STYLES}</style>
      <div className="gta-root">
        <div className="fu">
          <h2
            className="serif"
            style={{
              fontSize: 32,
              fontStyle: 'italic',
              fontWeight: 400,
              color: P.text,
              lineHeight: 1.15,
              marginBottom: 24,
            }}
          >
            {alreadyInApp ? 'You’re already here.' : 'Get the Haiven app.'}
          </h2>

          <p style={{
            fontSize: 14, color: P.textMid, lineHeight: 1.75,
            fontWeight: 300, marginBottom: 28,
          }}>
            {alreadyInApp
              ? 'You’re in the Haiven iOS app — Apple Health connects from Data connections.'
              : 'To sync your Apple Watch or Apple Health data, you’ll need the Haiven app.'}
          </p>
        </div>

        {!alreadyInApp && (
          <div className="fu d1">
            {installUrl ? (
              <a className="gta-btn" href={installUrl}>
                Install Haiven
              </a>
            ) : (
              <p style={{
                fontSize: 13, color: P.textMid, lineHeight: 1.7,
                fontWeight: 300, textAlign: 'center',
              }}>
                The iOS app is rolling out now &mdash; ping{' '}
                <a
                  href="mailto:hello@haiven.co"
                  style={{ color: P.primary, textDecoration: 'none' }}
                >
                  hello@haiven.co
                </a>{' '}
                and we&rsquo;ll send you the link the moment it&rsquo;s live.
              </p>
            )}
          </div>
        )}

        <div className="fu d2" style={{ textAlign: 'center', marginTop: 28 }}>
          <button
            onClick={handleKeepGoing}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: P.textMid, fontWeight: 300,
              fontFamily: "'DM Sans', sans-serif",
              padding: '8px 0', lineHeight: 1.7,
            }}
          >
            Prefer to keep going here? Your check-ins already power your
            signal &mdash; a wearable just adds depth.
          </button>
        </div>
      </div>
    </>
  );
}
